import React, { useContext, useMemo, useEffect, useCallback, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import Pane from 'components/Common/Pane'
import config from 'constants/config'
import { OFFER_TYPE_HOTEL_SLUG } from 'constants/offer'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import type { BaseRatePackageView } from 'selectors/offerPage/offerPageSelectors'
import {
  arePropertyRatesLoading,
  getBaseRatePackagesView,
  getFromDurationLabel,
  getPropertyMappedBedbankOffer,
  isAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import TextButton from 'components/Luxkit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'components/Luxkit/TextLink'
import WalledContent from 'components/Common/WalledContent'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Caption from 'components/Luxkit/Typography/Caption'
import BodyText from 'components/Luxkit/Typography/BodyText'
import GeoContext from 'contexts/geoContext'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import { isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import Group from 'components/utils/Group'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowFlightPackage from 'components/Luxkit/PricePoints/PriceRowFlightPackage'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setCustomerValue } from 'actions/AuthActions'
import { getCanViewLuxPlusBenefits, getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { unique } from 'lib/array/arrayUtils'
import { initialRooms } from 'lib/offer/occupancyUtils'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { feesToObject, priceWithoutPropertyFees } from 'lib/offer/pricing'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import useCheapestPropertyPricing from 'hooks/OfferPage/useCheapestPropertyPricing'
import {
  generateLoyaltyPointsCalculatorBedbankOptions,
  generateLoyaltyPointsCalculatorBundledFlightOptions,
  generateLoyaltyPointsCalculatorHotelOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import * as Analytics from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'

const SummaryPane = styled(Pane)`
  position: relative;

  > * + * {
    margin-top: ${rem(12)};
  }
`

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  margin-top: ${rem(20)};
  text-align: center;
`

interface Props {
  offer: App.HotelOffer;
  bedbankOffer?: App.BedbankOffer;
  className?: string;
  baseRatePackagesView: Array<BaseRatePackageView>;
  onViewPackageOptions: () => void;
  isLoading: boolean;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
  rooms?: Array<App.Occupants>;
  checkIn?: string;
  duration?: number;
  durationLabel: string;
  canViewLuxPlusBenefits: boolean;
  luxPlusLPPEnabled: boolean;
  availableForPurchase?: boolean;
}

function PropertySummary(props: Props) {
  const {
    offer,
    bedbankOffer,
    className,
    onViewPackageOptions,
    isLoading,
    flightOrigin,
    flightDestination,
    rooms,
    checkIn,
    duration,
    baseRatePackagesView,
    durationLabel,
    canViewLuxPlusBenefits,
    luxPlusLPPEnabled,
    availableForPurchase,
  } = props

  const { currentCurrency } = useContext(GeoContext)
  const offerDispatch = useOfferPageDispatch()
  const appDispatch = useAppDispatch()

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerDispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
  }, [offerDispatch])

  const [
    hotelPricing,
    hotelMemberPricing,
    pricingWithFlights,
    memberPricingWithFlights,
    rateView,
  ] = useCheapestPropertyPricing()

  const lpcPackage = rateView?.lpcRate?.package
  const bedbankRate = rateView?.bedbankRate?.rate
  const flightPrice =
    rateView?.lpcRate?.flightPrice ??
    (bedbankRate && pricingWithFlights && (pricingWithFlights.price - hotelPricing!.price)) ??
    0

  const saleUnit = useMemo(() => {
    if (offer.bundledWithFlightsOnly) return 'package*'
    return offer.saleUnit
  }, [offer.saleUnit, offer.bundledWithFlightsOnly])

  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && config.businessTraveller.currentAccountMode !== 'business'
  const hasFlights = !!(pricingWithFlights || memberPricingWithFlights) && !offer.flightCacheDisabled
  const hideNoCacheMessaging = !hasFlights && offer.bundledWithFlightsOnly
  const showIncludedGuestsExceeded = useMemo(() => {
    return !!lpcPackage && !lpcPackage.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], lpcPackage, offer)
  }, [lpcPackage, rooms, offer])
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const departureTooFarInFuture = useMemo(() => checkInMoment && checkInMoment.diff(moment.utc(), 'days') > 330, [checkInMoment])

  const [pricingToDisplay, memberPricingToDisplay] = useMemo(() => {
    let pricingToDisplay: App.PricingWithValue | undefined = hotelPricing
    let memberPricingToDisplay: App.PricingWithValue | undefined = hotelMemberPricing
    if (offer.bundledWithFlightsOnly) {
      if (flightsEnabled && !hideNoCacheMessaging && hasFlights) {
        pricingToDisplay = pricingWithFlights
        memberPricingToDisplay = memberPricingWithFlights
      } else {
        pricingToDisplay = undefined
        memberPricingToDisplay = undefined
      }
    }
    return [pricingToDisplay, memberPricingToDisplay]
  }, [
    hotelPricing,
    hotelMemberPricing,
    offer.bundledWithFlightsOnly,
    flightsEnabled,
    hideNoCacheMessaging,
    hasFlights,
    pricingWithFlights,
    memberPricingWithFlights,
  ])

  const fees = pricingToDisplay ? feesToObject(pricingToDisplay.fees) : {}

  // Only show LuxPlus+ badging if the member price is _better_ than the non-member price.
  const showMemberPrice =
    !!(pricingToDisplay?.price && memberPricingToDisplay?.price) &&
    memberPricingToDisplay.price < pricingToDisplay.price

  const canActuallyViewLuxPlusBenefits = canViewLuxPlusBenefits && (!bedbankRate || luxPlusLPPEnabled)
  const hotelValueOrHotelMemberValue = (canActuallyViewLuxPlusBenefits ? hotelMemberPricing?.value : hotelPricing?.value) ?? 0
  const hotelDiscountOrHotelMemberDiscount = (canActuallyViewLuxPlusBenefits ? hotelMemberPricing?.discountPercent : hotelPricing?.discountPercent) ?? 0
  const shouldShowValue =
    !!pricingToDisplay &&
    hotelValueOrHotelMemberValue > pricingToDisplay.price &&
    !lpcPackage?.roomRate.inclusionsHideValue

  const baseRatePackageView = useMemo(() => lpcPackage && baseRatePackagesView.find(
    (packageItem) => packageItem.duration === lpcPackage.duration && packageItem.id === lpcPackage.id,
  ), [baseRatePackagesView, lpcPackage])

  // These calculations are just for LPC rates.
  const baseRateValue = canViewLuxPlusBenefits ? baseRatePackageView?.memberValue : baseRatePackageView?.value
  const inclusionsAmount = (canViewLuxPlusBenefits ? lpcPackage?.totalInclusionsAmount : lpcPackage?.inclusionsAmount) ?? 0
  const pkgValueOrPkgMemberValue = (canViewLuxPlusBenefits ? lpcPackage?.memberValue : lpcPackage?.value) ?? 0

  // We use base rate cost to calculate new totalValue and discountPercent, using the isBaseRate flag on rate plan level.
  // This shows a bigger discount
  const valuedUpTo = useMemo(() => {
    if (baseRateValue && (baseRateValue + inclusionsAmount) > pkgValueOrPkgMemberValue) {
      const displayedHotelValue = baseRateValue + inclusionsAmount + (fees.property ?? 0)
      const displayedHotelDiscountPercent = getValueOffPercent(displayedHotelValue, hotelPricing?.price ?? 0)
      return { value: displayedHotelValue, percent: displayedHotelDiscountPercent }
    }

    return { value: hotelValueOrHotelMemberValue, percent: hotelDiscountOrHotelMemberDiscount }
  }, [
    baseRateValue,
    inclusionsAmount,
    pkgValueOrPkgMemberValue,
    hotelValueOrHotelMemberValue,
    hotelDiscountOrHotelMemberDiscount,
    fees.property,
    hotelPricing?.price,
  ])

  const shouldShowDiscountPercent = valuedUpTo.percent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const showDiscountPill = !offer?.isDiscountPillHidden && shouldShowDiscountPercent && !isLoading && !offer?.bundledWithFlightsOnly

  const showPriceDetails = !!fees.taxesAndFees || !!fees.property || config.agentHub.isEnabled
  const checkOut = checkInMoment ? checkInMoment.clone().add(lpcPackage?.duration ?? duration, 'days').format(ISO_DATE_FORMAT) : undefined
  const offerAvailableRates = useOfferAvailableRates(offer?.id, {
    checkIn,
    checkOut,
    rooms: rooms ?? initialRooms,
  })

  const viewOptionsLabel = `View ${config.brandConfig.travelSynonym} options`

  const pkgAvailableRate = useMemo(() => offerAvailableRates?.rates?.find(rate => rate.packageUniqueKey === lpcPackage?.uniqueKey), [lpcPackage, offerAvailableRates])
  const showSecureWithDepositTag = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, hotelPricing?.price, lpcPackage, pkgAvailableRate)
  const showHotelPrice = !offer.bundledWithFlightsOnly || (flightsEnabled && !hideNoCacheMessaging && hasFlights)

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  const canShowBedbankFlightBundlePrice = flightsEnabled && hasFlights && !departureTooFarInFuture
  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (rateView?.lpcRate) {
      const packageView = rateView.lpcRate
      return [
        generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView }),
        generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
          packageView,
          skip: !offer.bundledWithFlightsOnly || !hasFlights || !flightsEnabled,
        }),
      ]
    }
    if (bedbankRate) {
      return [
        generateLoyaltyPointsCalculatorBedbankOptions(bedbankRate, {
          price: hotelPricing?.price,
          memberPrice: hotelMemberPricing?.price,
          propertyFees: bedbankRate.totals.propertyFees ?? 0,
        }),
        generateLoyaltyPointsCalculatorBundledFlightOptions(bedbankOffer, {
          flightPrice,
          skip: !offer.bundledWithFlightsOnly || !canShowBedbankFlightBundlePrice,
        }),
      ]
    }
    return []
  }, [
    rateView,
    offer,
    hasFlights,
    flightsEnabled,
    bedbankRate,
    hotelPricing,
    hotelMemberPricing,
    bedbankOffer,
    flightPrice,
    canShowBedbankFlightBundlePrice,
  ])

  useEffect(() => {
    appDispatch(setCustomerValue(hotelPricing?.price ?? 0))
  }, [appDispatch, hotelPricing?.price])

  const showPrices = !!hotelPricing

  const shouldTrackPrice = !isLoading && availableForPurchase && showPrices && showHotelPrice && !!pricingToDisplay?.price
  const priceTrackedRef = useRef(false)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()

  useEffect(() => {
    if (shouldTrackPrice && searchId && offer?.id && !priceTrackedRef.current) {
      Analytics.trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: pricingToDisplay.price.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, pricingToDisplay?.price, offer?.id])

  return <SummaryPane border="neutral-six" borderRadius="S" padding="28 20" shadow="bottom-small" className={className} data-testid="SummaryPane">
    {availableForPurchase && <WalledContent
      enforced={offer.walledGarden}
      wall={<BodyText variant="large" weight="bold">{offer.durationLabel} packages available</BodyText>}
    >
      {showPrices && <Group direction="vertical" horizontalAlign="center" gap={8}>
        <Group direction="vertical" horizontalAlign="center" gap={4}>
          <Group direction="vertical" horizontalAlign="center">
            <LoadingIndicator floating backdrop="white" opaque visible={isLoading} />
            {showSecureWithDepositTag && <SecureWithDepositTagAndTooltip offerType={offer.type} /> }
            {offer.bundledWithFlightsOnly && <CaptionTextBlock variant="large" weight="bold" startIcon={<LinePlaneFlyIcon />}>
              Hotel + flights{hideNoCacheMessaging && ' packages available'}
            </CaptionTextBlock>}
            {showHotelPrice && pricingToDisplay && <>
              <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="L"
                price={pricingToDisplay.price}
                memberPrice={memberPricingToDisplay?.price}
                saleUnit={saleUnit}
                testid="hotel-summary-price"
                horizontalAlign="center"
                offerConfig={offer.luxPlus}
              />
            </>}
            {showHotelPrice && <PriceRowTaxesAndFees
              endIcon={showPriceDetails && <OfferPriceDetailsRow
                offerPage
                offer={offer}
                duration={duration ?? lpcPackage?.duration ?? bedbankRate!.nights}
                propertyFees={fees.property}
                taxesAndFees={fees.taxesAndFees}
                flightPrice={offer.bundledWithFlightsOnly ? flightPrice : undefined}
                hotelPrice={hotelPricing ? priceWithoutPropertyFees(hotelPricing) : 0}
                rooms={rooms}
                extraGuestMessage={showIncludedGuestsExceeded ? lpcPackage!.roomPolicyDescription : ''}
                dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                trigger="info-icon"
                cta={availableForPurchase && <TextButton
                  kind="primary"
                  fit="flex"
                >
                  {viewOptionsLabel}
                </TextButton>
                }
                onCtaAfterClose={onViewPackageOptions}
              />}
            />}
            {showIncludedGuestsExceeded &&
              <AdditionalGuestsPopup
                complex={false}
                description="+ Extra guest surcharge"
                modalContent={lpcPackage!.roomPolicyDescription}
              />
            }
            {shouldShowValue && <PriceRowValueDiscountWithCaption
              size="M"
              originalValue={valuedUpTo.value}
              discountPercentage={showDiscountPill ? valuedUpTo.percent : undefined}
            />}
          </Group>
          <LoyaltyPointsEarn
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            horizontalAlign="center"
          />
          {flightsEnabled && <Group direction="vertical" horizontalAlign="center">
            {!hasFlights && flightOrigin.code === flightDestination?.code && (
              <ChangeDepartureWrapper>
                <Caption variant="large" colour="neutral-three">
                  No flights available from {flightOrigin.name}. Please select another airport.
                </Caption>
                <TextLink
                  onClick={onToggleFilter}
                  data-filter="airport"
                >
                  Change departure airport
                </TextLink>
              </ChangeDepartureWrapper>
            )}
            {!hasFlights && flightOrigin.code !== flightDestination?.code && !departureTooFarInFuture && (
              <Caption variant="large" colour="neutral-three" align="center">
                Add flights to the hotel package. Select from all major airlines at the next step.
              </Caption>
            )}
            {!hasFlights && departureTooFarInFuture && (
              <ChangeDepartureWrapper>
                <Caption variant="large" colour="neutral-three">
                  Flights can only be booked up to 11 months in advance.
                </Caption>
              </ChangeDepartureWrapper>
            )}
            {hasFlights && !offer?.bundledWithFlightsOnly && <PriceRowFlightPackage
              data-testid="FlightPrice"
              product="hotel"
              price={(showMemberPrice ? memberPricingToDisplay.price : pricingToDisplay!.price) + flightPrice}
            />}
            <AtolProtectedTooltip />
          </Group>}
          <Group direction="vertical" horizontalAlign="center" noDisplayWhenEmpty>
            {lpcPackage && config.businessTraveller.currentAccountMode === 'business' && pricingToDisplay && <BusinessTravellerOfferCreditsTextLink
              type="estimate"
              offer={offer}
              totalCost={pricingToDisplay.price}
              numberOfNights={lpcPackage.duration}
              roomCount={rooms?.length}
            />}
            {bedbankRate && config.businessTraveller.currentAccountMode === 'business' && pricingToDisplay && <BusinessTravellerOfferCreditsTextLink
              type="estimate"
              offer={bedbankOffer!}
              totalCost={pricingToDisplay.price}
              numberOfNights={bedbankRate.nights}
              roomCount={rooms?.length}
            />}
          </Group>
        </Group>
        {lpcPackage && <PriceRowAgentHubCommission
          /* Based on `<AlwaysOnRateOption/>`. */
          size="L"
          offerId={offer.id}
          offerLocations={offerLocations}
          vendorName={vendorName}
          productType={offer.productType}
          packageId={lpcPackage.id}
        />}
        {bedbankRate && <PriceRowAgentHubCommission
          /* Based on `<BedbankPackageTilePricing/>`. */
          size="L"
          offerId={bedbankOffer!.id}
          offerLocations={offerLocations}
          vendorName={vendorName}
          productType={bedbankOffer!.productType}
          packageId={bedbankRate.id}
        />}

      </Group>}
    </WalledContent>}
    {availableForPurchase && <TextButton
      kind="primary"
      data-testid="OfferAvailable"
      onClick={onViewPackageOptions}
      fit="flex"
    >
      {viewOptionsLabel}
    </TextButton>}
    {!availableForPurchase && <>
      <BodyText
        variant="large"
        data-testid="OfferSoldOut"
        weight="bold"
      >
        This offer has SOLD OUT
      </BodyText>
      <TextButton
        kind="primary"
        to={`/${OFFER_TYPE_HOTEL_SLUG}`}
        fit="flex"
      >
        Browse all hotel offers
      </TextButton>
    </>}
  </SummaryPane>
}

export default withOfferPageAndReduxState((state, appState) => ({
  isLoading: arePropertyRatesLoading(state, appState),
  baseRatePackagesView: getBaseRatePackagesView(appState, state),
  checkIn: state.checkInDate,
  duration: state.duration,
  offer: state.offer,
  bedbankOffer: getPropertyMappedBedbankOffer(state, appState),
  flightOrigin: state.airport,
  rooms: state.rooms,
  durationLabel: getFromDurationLabel(state, appState),
  canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(appState),
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
  open: !!state.activeFilter,
}), PropertySummary, preventRenderWithOpenFilter)
